// 폭발 플립북: 카메라를 바라보는 사각 한 장이 8×8 시트(blastSheet.ts)의 64프레임을 수명 동안 순서대로 재생한다. 연속 두 프레임을 섞어 움직임이 부드럽다.
// 지름 = length(m). 위치(x,y,z)가 중심이다(y에 높이를 준다). 슬롯마다 시트를 무작위 각도로 돌려(seed) 같은 폭발이 겹쳐도 달라 보인다.
// 불(R 채널)은 color→color2로 달아오르는 발광, 연기(G 채널)는 어두운 알파 블렌딩이라 한 번의 드로우콜로 불 + 연기가 함께 나온다.
// 쓰는 칸: length = 지름(m), y = 중심 높이, life = 재생 시간(초), variant 0 = 불 + 연기 / 1 = 연기 위주(불 40%),
//          color(불 바깥)·color2(불 핵)·intensity·seed. 드로우콜 1.
// 빌보드는 정점 셰이더에서 한다(정점 4개가 같은 중심, 모서리 오프셋을 시야 공간에서 더함): 지오메트리를 카메라마다 다시 쓰지 않는다.
import * as THREE from 'three/webgpu';
import { cameraProjectionMatrix, clamp, cos, float, floor, min, mix, mod, modelViewMatrix, positionGeometry, sin, texture, uv, vec2, vec3, vec4 } from 'three/tsl';
import { BatchModule, fxAttributes, type Slot } from './batch';
import { quadLayout } from './layout';
import { BLAST_FRAME_PX, BLAST_FRAMES, BLAST_GRID, blastFrame, blastSheetData } from './blastSheet';
import { fxMaterial } from './material';
import { clamp01 } from './path';
import { MODULE_CAPS, type ModuleKind } from './types';

export { BLAST_FRAMES, BLAST_GRID, blastFrame };

/** 시트 텍스처(모듈 라이브러리가 소유하고 dispose한다). 데이터는 프로세스 전체가 공유한다 */
export function createBlastTexture(): THREE.DataTexture {
  const px = BLAST_FRAME_PX * BLAST_GRID;
  const t = new THREE.DataTexture(blastSheetData(), px, px, THREE.RGBAFormat, THREE.UnsignedByteType);
  t.wrapS = THREE.ClampToEdgeWrapping;
  t.wrapT = THREE.ClampToEdgeWrapping;
  t.magFilter = THREE.LinearFilter;
  t.minFilter = THREE.LinearFilter;
  t.generateMipmaps = false;
  t.colorSpace = THREE.NoColorSpace;
  t.needsUpdate = true;
  return t;
}

function makeMaterial(sheet: THREE.Texture): THREE.MeshBasicNodeMaterial {
  const { ctl, t1, t2 } = fxAttributes();
  const age = ctl.x, env = ctl.y, seed = ctl.z, variant = ctl.w;

  // 정점: 중심 + 시야 공간 모서리(지름 = aTint2.w), 슬롯별 무작위 회전
  const c = uv().sub(0.5).mul(t2.w);
  const a = seed.mul(6.2832);
  const ca = cos(a), sa = sin(a);
  const corner = vec2(c.x.mul(ca).sub(c.y.mul(sa)), c.x.mul(sa).add(c.y.mul(ca)));
  const mv = modelViewMatrix.mul(vec4(positionGeometry, 1));
  const vertex = cameraProjectionMatrix.mul(vec4(mv.x.add(corner.x), mv.y.add(corner.y), mv.z, mv.w));

  // 프레임 두 장을 섞는다. 프레임 안쪽 좌표는 가장자리 반 픽셀을 비워 이웃 칸이 새지 않게 한다
  const f = clamp(age, 0, 0.9999).mul(BLAST_FRAMES);
  const f0 = floor(f);
  const fr = f.sub(f0);
  const f1 = min(f0.add(1), BLAST_FRAMES - 1);
  const pad = 0.5 / BLAST_FRAME_PX;
  const local = clamp(uv(), pad, 1 - pad);
  const sampleAt = (fi: typeof f0) => {
    const colI = mod(fi, BLAST_GRID);
    const rowI = floor(fi.div(BLAST_GRID));
    const origin = vec2(colI.div(BLAST_GRID), float(1).sub(rowI.add(1).div(BLAST_GRID)));
    return texture(sheet, origin.add(local.div(BLAST_GRID)));
  };
  const rg = mix(sampleAt(f0), sampleAt(f1), fr);
  const fire = rg.x.mul(float(1).sub(variant.mul(0.6)));
  const smoke = rg.y;
  const hot = fire.pow(1.6);
  const fireCol = mix(t1.rgb, t2.rgb, hot).mul(t1.w).mul(fire.mul(0.9).add(0.1));
  const smokeCol = t1.rgb.mul(0.05).add(vec3(0.03, 0.028, 0.026));
  const col = mix(smokeCol, fireCol, clamp(fire.mul(2), 0, 1));
  const alpha = clamp(smoke.mul(variant.mul(0.35).add(1)).mul(0.7).max(fire.mul(1.15)), 0, 1).mul(env);

  const m = fxMaterial(col, alpha, { blending: THREE.NormalBlending });
  m.vertexNode = vertex;
  return m;
}

export class BlastModule extends BatchModule {
  readonly kind: ModuleKind = 'blast';

  constructor(sheet: THREE.Texture, cap = MODULE_CAPS.blast) {
    super(cap, quadLayout(), makeMaterial(sheet), 25, 'fx-blast');
  }

  /** 살아 있는 슬롯 i가 지금 재생하는 프레임 번호(QA·테스트) */
  frameOfSlot(i: number): number {
    const s = this.slots[i];
    return blastFrame(s.age / s.life);
  }

  protected positions(s: Slot, out: Float32Array, o: number): void {
    const sin = Math.sin(s.yaw), cos = Math.cos(s.yaw);
    const x = s.x + sin * s.ahead, z = s.z + cos * s.ahead;
    // 네 정점이 같은 중심(모서리는 셰이더가 만든다)
    for (let v = 0; v < 4; v++) { out[o + v * 3] = x; out[o + v * 3 + 1] = s.y; out[o + v * 3 + 2] = z; }
  }

  protected ctl(s: Slot, out: Float32Array): void {
    out[0] = clamp01(s.age / s.life);
    out[1] = Math.min(1, s.age / 0.012);
    out[2] = s.seed;
    out[3] = s.variant >= 0.5 ? 1 : 0;
  }

  /** aTint2.w = 지름(m) */
  protected override tint2w(s: Slot): number { return s.length * s.scale; }
}
